import { styled } from '@linaria/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  type DotProps,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ModelComparisonFamily,
  ModelComparisonResponse,
} from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, skyLight } from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';
import { LineChartDot } from '../../process-analysis/components/line-chart/line-chart-dot';
import { LineChartTooltip } from '../../process-analysis/components/line-chart/line-chart-tooltip';
import type { ChartDataItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { ChartLegend } from '../model-comparison/cycle-time-chart';
import { buildItemColors, buildLineItems } from '../model-comparison/model-comparison.helpers';
import type { SessionsDrilldownWindow } from '../model-comparison/model-comparison.type';
import { buildTokenChartData, formatTokens } from './usage.helpers';
import type { UsageMeasure } from './usage.type';

/**
 * Extracts the inclusive period window carried on a chart datum so a click can
 * scope the sessions drill-down to the period that point represents, rather
 * than the whole selected window. Returns undefined when the bounds are
 * missing or malformed.
 *
 * @param {ChartDataItem | undefined} payload - The clicked datum's row
 * @returns {SessionsDrilldownWindow | undefined} The period window, if valid
 */
const windowFromRow = (payload: ChartDataItem | undefined): SessionsDrilldownWindow | undefined => {
  const start = payload?.periodStart;
  const end = payload?.periodEnd;
  return typeof start === 'string' && typeof end === 'string' ? { start, end } : undefined;
};

type Props = {
  response: ModelComparisonResponse;
  /**
   * The full, provider-filter-unaware family list, used only to seed stable
   * color assignment — `response` itself may already be narrowed by the
   * provider filter.
   */
  allFamilies: ModelComparisonFamily[];
  /** The token measure the chart projects out of each period's bucket. */
  measure: UsageMeasure;
  /** Render one line per family, or grouped bars per period. Defaults to line. */
  variant?: 'line' | 'bar';
  /** Y-axis label for the current measure (already translated). */
  axisLabel: string;
  /** Controlled set of families whose lines are hidden (shared via the URL). */
  hiddenFamilies?: Set<string>;
  /** Controlled-mode legend toggle handler; pairs with hiddenFamilies. */
  onToggleFamily?: (family: string) => void;
  /**
   * Opens the sessions side panel for the clicked point's model family,
   * narrowed to the period that point represents (a line dot or a bar). The
   * family is the clicked series; the window comes from the clicked datum's
   * period bounds.
   */
  onSelectFamily?: (modelFamily: string, window?: SessionsDrilldownWindow) => void;
  /**
   * When true, the chart's own title/subtitle header is suppressed because a
   * parent (the Token-usage view) renders a shared header with the chart/table
   * toggle. The chart then fills the whole area below that header.
   */
  embedded?: boolean;
};

/**
 * Token usage per period by model family, drawn as one line per family
 * (`variant="line"`) or grouped bars per period (`variant="bar"`), with the
 * selected measure (total / input / output / cache read / cache write) on the
 * Y axis. All families appear in the legend with a checkbox to show/hide each
 * series (visibility is shared with the filter panel's select/deselect-all via
 * the URL). Line periods without turns are bridged (connectNulls) so each line
 * stays continuous. The period size follows the sidebar's time-allocation
 * filter.
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element} The chart card content
 */
export const UsageChart = ({
  response,
  allFamilies,
  measure,
  variant = 'line',
  axisLabel,
  hiddenFamilies,
  onToggleFamily,
  onSelectFamily,
  embedded = false,
}: Props) => {
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [internalHiddenIds, setInternalHiddenIds] = useState<Set<string>>(new Set());
  const hiddenIds = hiddenFamilies ?? internalHiddenIds;

  const lineItems = useMemo(() => buildLineItems(response.families), [response.families]);
  const chartData = useMemo(() => buildTokenChartData(response, measure), [response, measure]);
  // Colors are assigned by index over the provider-filter-UNAWARE full list so
  // a family's color never shifts as the provider selection changes — same
  // rationale as the cycle-time chart.
  const itemColors = useMemo(() => buildItemColors(buildLineItems(allFamilies)), [allFamilies]);

  const toggleItem = (id: string) => {
    if (onToggleFamily) {
      onToggleFamily(id);
      return;
    }
    setInternalHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleItems = lineItems.filter((item) => !hiddenIds.has(item.id));
  const isHighlighted = (itemId: string) => hoveredItem === null || hoveredItem === itemId;

  return (
    <ChartLayout data-testid="usage-chart" style={embedded ? { minHeight: 0 } : undefined}>
      {!embedded && (
        <Header>
          <H3>{t('agent_miner.usage.chart_title')}</H3>
          <Text size="small" color={inkLight}>
            {t('agent_miner.usage.chart_subtitle')}
          </Text>
        </Header>
      )}
      <ChartArea>
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 10, bottom: 24 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              tickFormatter={(value) => formatTokens(value as number)}
              width={64}
              label={{
                value: axisLabel,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
            <RechartsTooltip content={<LineChartTooltip formatValue={(value) => formatTokens(value)} />} />
            {visibleItems.map((item) => {
              // The family is the series id (closure); the window comes from the
              // clicked datum's own period, so the drill-down is scoped to the
              // period that point represents rather than the whole window.
              const handleDotSelect = onSelectFamily
                ? (dotProps: DotProps) => {
                    // onPointSelect (not onClick): Recharts rewrites event-named
                    // props on activeDot, dropping payload — see LineChartDot.
                    const { payload } = dotProps as DotProps & { payload?: ChartDataItem };
                    onSelectFamily(item.id, windowFromRow(payload));
                  }
                : undefined;
              return variant === 'bar' ? (
                <Bar
                  key={item.id}
                  dataKey={item.id}
                  name={item.name}
                  fill={itemColors[item.id]}
                  fillOpacity={isHighlighted(item.id) ? 1 : 0.3}
                  isAnimationActive={false}
                  cursor={onSelectFamily ? 'pointer' : undefined}
                  onClick={
                    onSelectFamily
                      ? (barData: { payload?: ChartDataItem }) =>
                          onSelectFamily(item.id, windowFromRow(barData?.payload))
                      : undefined
                  }
                />
              ) : (
                <Line
                  key={item.id}
                  type="monotone"
                  dataKey={item.id}
                  name={item.name}
                  stroke={itemColors[item.id]}
                  strokeWidth={2}
                  strokeOpacity={isHighlighted(item.id) ? 1 : 0.3}
                  dot={
                    <LineChartDot
                      color={itemColors[item.id]}
                      opacity={isHighlighted(item.id) ? 1 : 0.3}
                      onPointSelect={handleDotSelect}
                    />
                  }
                  activeDot={
                    <LineChartDot
                      color={itemColors[item.id]}
                      opacity={isHighlighted(item.id) ? 1 : 0.3}
                      onPointSelect={handleDotSelect}
                    />
                  }
                  isAnimationActive={false}
                  // connectNulls: bridge periods with no turns so the usage
                  // trend line stays continuous across occasional empty periods
                  // rather than fragmenting into disconnected dots.
                  connectNulls
                />
              );
            })}
            <Legend
              content={
                <ChartLegend
                  items={lineItems}
                  chartData={chartData}
                  hiddenIds={hiddenIds}
                  hoveredItem={hoveredItem}
                  itemColors={itemColors}
                  onToggle={toggleItem}
                  setHoveredItem={setHoveredItem}
                />
              }
              verticalAlign="bottom"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartArea>
    </ChartLayout>
  );
};

const ChartLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

/* The chart area must own the remaining vertical space or the chart collapses
   to the ResponsiveContainer's 160px floor. */
const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;
